// Developer-Komponente: Wache-Logo verwalten.
// Ersetzt das Default-Feuerwache-Online-Logo durch ein eigenes für diese Wache.
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Image as ImageIcon, Upload, RotateCcw, Loader2 } from 'lucide-react';
import { developerApi, wacheApi } from '../../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const DEFAULT_LOGO = '/logo-fwonline.svg';

export function WacheLogoSection() {
  const [logoUrl, setLogoUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await wacheApi.get();
      setLogoUrl(res.data?.logo_url || null);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleUpload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Datei zu groß (max. 5 MB)');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await developerApi.uploadWacheLogo(fd);
      toast.success('Logo aktualisiert');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload fehlgeschlagen');
    } finally {
      setUploading(false);
    }
  };

  const handleReset = async () => {
    try {
      await developerApi.resetWacheLogo();
      toast.success('Logo auf Standard zurückgesetzt');
      await load();
    } catch {
      toast.error('Zurücksetzen fehlgeschlagen');
    }
  };

  const isCustom = !!logoUrl;
  // Cache-Buster damit Browser nach Upload neu lädt
  const previewUrl = logoUrl ? `${logoUrl}?v=${Date.now()}` : DEFAULT_LOGO;

  return (
    <Card data-testid="developer-wache-logo-section">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ImageIcon className="w-5 h-5" />
          Wache-Logo
        </CardTitle>
        <CardDescription>
          Dieses Logo wird oben links in der Sidebar und auf dem Login-Screen
          dieser Wache angezeigt. Ohne eigenes Bild wird das Standard-Feuerwache-Logo verwendet.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col sm:flex-row gap-4 items-start">
          <div className="shrink-0 w-32 h-32 rounded-lg border bg-slate-50 flex items-center justify-center overflow-hidden">
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            ) : (
              <img
                src={previewUrl}
                alt="Wache-Logo"
                className="max-h-full max-w-full object-contain"
                onError={(e) => { e.target.src = DEFAULT_LOGO; }}
              />
            )}
          </div>

          <div className="flex-1 space-y-3">
            <div className="flex items-center gap-2">
              {isCustom ? (
                <Badge>Custom</Badge>
              ) : (
                <Badge variant="outline">Standard (Feuerwache Online)</Badge>
              )}
            </div>

            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              data-testid="wache-logo-file-input"
              onChange={(e) => {
                handleUpload(e.target.files?.[0]);
                e.target.value = '';
              }}
            />

            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={uploading}
                onClick={() => fileRef.current?.click()}
                data-testid="wache-logo-upload-btn"
              >
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
                {isCustom ? 'Ersetzen' : 'Logo hochladen'}
              </Button>
              {isCustom && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleReset}
                  data-testid="wache-logo-reset-btn"
                >
                  <RotateCcw className="w-3.5 h-3.5 mr-1" />
                  Standard
                </Button>
              )}
            </div>

            <p className="text-xs text-muted-foreground">
              Max. 5 MB. Formate: PNG, JPEG, WebP, SVG. Empfohlen: quadratisches Bild
              oder SVG mit transparentem Hintergrund.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default WacheLogoSection;
